import React from 'react';
import { clsx } from 'clsx';
import styles from './InputCheckbox.module.css';
import { FormErrorMessage } from '../form-error-message';

export type InputCheckBoxProps = React.DetailedHTMLProps<
	React.InputHTMLAttributes<HTMLInputElement>,
	HTMLInputElement
> & {
	/**
	 * Indicates the text or node to be used in the input label
	 */
	label: React.ReactNode;

	/**
	 * Name of the input
	 * @type string
	 */
	name?: string;

	/**
	 * Indicates error message to be displayed
	 */
	errorMessage?: React.ReactNode;

	/**
	 * Identifier of the component
	 */
	id?: string;

	/**
	 * Styles of the container of the input
	 */
	className?: string;
};

export const InputCheckbox = React.forwardRef<HTMLInputElement, InputCheckBoxProps>(
	({ label, errorMessage, name, id, className, ...props }, ref) => {
		const generateIdentifier = React.useId();
		const identifier = id ?? generateIdentifier;
		const errorMessageIdentifier = errorMessage ? identifier + '-error' : undefined;

		return (
			<div className={clsx(styles.contentInput, className)}>
				<div className={styles.inputContainer}>
					<input
						className={clsx(styles.input)}
						ref={ref}
						type="checkbox"
						name={name}
						id={identifier + '-input'}
						aria-errormessage={errorMessageIdentifier}
						aria-invalid={Boolean(errorMessage)}
						{...props}
					/>
					<label className={styles.label} htmlFor={identifier + '-input'}>
						{label}
					</label>
				</div>
				{errorMessage && <FormErrorMessage id={errorMessageIdentifier}>{errorMessage}</FormErrorMessage>}
			</div>
		);
	}
);
InputCheckbox.displayName = 'InputCheckbox';
